/**
 * ChartPane — one pane of the signal chart stack.
 *
 * Every pane renders from the same row array and shares a `syncId`, which is
 * what keeps the crosshair and the tooltip in step across the stack.
 *
 * A pane is also the unit of y-scale: one pane, one axis. Series of different
 * scale get their own pane rather than a second axis, because the alignment of
 * two y-scales on one plot is arbitrary and invents a correlation that is not
 * in the data.
 */
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Cell,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import CrosshairTooltip from './CrosshairTooltip';
import {
  PANE_PRICE,
  PANE_VOLUME,
  formatAxisTick,
  shortDate,
} from '../../lib/signalSeries';

const GRID = 'hsl(var(--border))';
const AXIS_TICK = { fill: 'hsl(var(--muted-foreground))', fontSize: 10 };

export default function ChartPane({
  pane,
  rows,
  allSeries,
  visible,
  signalDate,
  evaluations,
  blockers,
  showXAxis = false,
  annotations = [],
  matchDots = [],
  onActiveChange,
}) {
  const series = pane.series.filter((s) => visible[s.id] !== false);
  const isPrice = pane.kind === PANE_PRICE;
  // A non-price pane with everything toggled off has nothing to show; the
  // price pane always has the close line.
  if (!series.length && !isPrice) return null;

  const isVolume = pane.kind === PANE_VOLUME;
  const bands = series.filter((s) => s.kind === 'band_upper' && s.band_group);

  return (
    <div className="w-full" style={{ height: pane.height }}>
      <div className="flex items-baseline justify-between px-1 pb-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {pane.title}
        </span>
      </div>

      <ResponsiveContainer width="100%" height={pane.height - 16}>
        <ComposedChart
          data={rows}
          syncId="signal-chart"
          margin={{ top: 4, right: 8, left: -18, bottom: showXAxis ? 0 : -8 }}
          // Lift the hovered date so the match panel tracks the same bar the
          // crosshair is on. syncId keeps the panes aligned with each other,
          // but it does not reach anything outside the chart.
          onMouseMove={(state) => onActiveChange?.(state?.activeLabel ?? null)}
          onMouseLeave={() => onActiveChange?.(null)}
        >
          <CartesianGrid
            stroke={GRID}
            strokeOpacity={0.45}
            vertical={false}
          />

          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={showXAxis ? AXIS_TICK : false}
            height={showXAxis ? 20 : 0}
            tickFormatter={shortDate}
            minTickGap={36}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            width={48}
            tick={AXIS_TICK}
            domain={pane.domain || ['auto', 'auto']}
            tickFormatter={formatAxisTick(pane.kind)}
            allowDecimals={!isVolume}
          />

          {/* Threshold guides — solid hairlines, one shade off the surface. */}
          {(pane.guides || []).map((guide) => (
            <ReferenceLine
              key={guide}
              y={guide}
              stroke={GRID}
              strokeOpacity={0.9}
            />
          ))}

          {/* Spans where a state attribute held, or an event stayed active. */}
          {isPrice &&
            annotations.map((span, i) => (
              <ReferenceArea
                key={`${span.id}-${i}`}
                x1={span.from}
                x2={span.to}
                fill={span.color}
                fillOpacity={0.07}
                ifOverflow="hidden"
              />
            ))}

          {/* The band fill goes down first, so lines read on top of it. */}
          {bands.map((band) => (
            <Area
              key={`${band.band_group}__range`}
              dataKey={`${band.band_group}__range`}
              stroke="none"
              fill={band.color}
              fillOpacity={0.08}
              isAnimationActive={false}
              activeDot={false}
              legendType="none"
              tooltipType="none"
              connectNulls={false}
            />
          ))}

          {isVolume &&
            series
              .filter((s) => s.kind === 'volume')
              .map((s) => (
                <Bar key={s.id} dataKey={s.id} isAnimationActive={false}>
                  {rows.map((row, i) => (
                    <Cell
                      key={i}
                      fill={
                        i > 0 && rows[i - 1].close != null && row.close < rows[i - 1].close
                          ? 'hsl(var(--loss))'
                          : 'hsl(var(--win))'
                      }
                      fillOpacity={0.45}
                    />
                  ))}
                </Bar>
              ))}

          {series
            .filter((s) => s.kind === 'histogram')
            .map((s) => (
              <Bar
                key={s.id}
                dataKey={s.id}
                fill={s.color}
                fillOpacity={0.35}
                isAnimationActive={false}
                name={s.label}
              />
            ))}

          {isPrice && (
            <Line
              dataKey="close"
              name="Close"
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}

          {series
            .filter((s) => !['histogram', 'volume'].includes(s.kind))
            .map((s) => (
              <Line
                key={s.id}
                dataKey={s.id}
                name={s.label}
                stroke={s.color}
                strokeWidth={s.declared === false ? 1 : 2}
                strokeOpacity={s.declared === false ? 0.5 : 1}
                strokeDasharray={s.dash}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            ))}

          {/* Bars the screener passed on. */}
          {isPrice &&
            matchDots.map((dot) => (
              <ReferenceDot
                key={dot.date}
                x={dot.date}
                y={dot.close}
                r={3}
                fill="hsl(var(--win))"
                stroke="hsl(var(--background))"
                strokeWidth={2}
                ifOverflow="hidden"
              />
            ))}

          {signalDate && (
            <ReferenceLine
              x={signalDate}
              stroke="hsl(var(--primary))"
              strokeDasharray="4 3"
              strokeWidth={1.5}
              ifOverflow="hidden"
              label={
                isPrice
                  ? {
                      value: 'SIGNAL',
                      position: 'top',
                      fill: 'hsl(var(--primary))',
                      fontSize: 9,
                      fontWeight: 700,
                    }
                  : undefined
              }
            />
          )}

          <Tooltip
            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1 }}
            content={(props) => (
              <CrosshairTooltip
                {...props}
                series={allSeries}
                visible={visible}
                evaluation={evaluations[props.label]}
                blockers={blockers[props.label]}
              />
            )}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
